/**
 * <sample-gifting> — Phase 2 cart-drawer gift picker.
 *
 * Mirrors assets/gift-wrapping.js: mutates the cart with
 * fetch(FoxTheme.routes.cart_update_url, { ...fetchConfig(), body: { updates, attributes, sections } })
 * then publishes PUB_SUB_EVENTS.cartUpdate so cart.js re-renders the drawer.
 *
 * The element lives inside #CartDrawerBody, so cart.js replaces its innerHTML on
 * every cart change. connectedCallback therefore re-runs against fresh Liquid config
 * (qualifies, cart.attributes) and reconciles desired vs actual gift state each time.
 *
 * IMPORTANT: this element only ADDS the real sample line at its real price. The £0.00
 * is delivered later by a native Buy-X-Get-Y discount that does not exist yet. Never
 * fake a £0.00 price here.
 */
class SampleGifting extends HTMLElement {
  // Shared across instances (the element is destroyed/recreated on every re-render).
  static busy = false;

  connectedCallback() {
    const configEl = this.querySelector('[data-sample-gifting-config]');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('[sample-gifting] bad config JSON', e);
      return;
    }

    if (!this.config.enabled) return;
    if (typeof FoxTheme === 'undefined') return;

    // A mutation is in flight; a fresh instance will reconcile once it lands.
    if (SampleGifting.busy) return;

    this.giftVariantIds = (this.config.giftVariantIds || []).map(String);
    this.poolVariantIds = (this.config.pool || []).map((p) => String(p.variantId));

    this.markGiftLines();
    this.applyBadges();
    this.reconcile();
    this.applyFreePricing();
  }

  /**
   * Tag gift lines so CSS can strip the quantity stepper and the (pink) discount
   * pill from them: a free gift's quantity is fixed by the picker, and its discount
   * string is noise on the line (proposal section 7). Runs on every cart re-render.
   */
  markGiftLines() {
    const giftSet = new Set(this.giftVariantIds);
    document.querySelectorAll('.cart-item[data-variant-id]').forEach((line) => {
      line.classList.toggle('cart-item--gift', giftSet.has(String(line.dataset.variantId)));
    });
  }

  /** Variant ids of every line currently in the cart (read from the DOM). */
  cartVariantIds() {
    return Array.from(document.querySelectorAll('.cart-item[data-variant-id]')).map((el) =>
      String(el.dataset.variantId)
    );
  }

  reconcile() {
    const cfg = this.config;
    const inCart = this.cartVariantIds();

    // ---- Not qualifying: trigger-removal cleanup ----
    if (!cfg.qualifies) {
      const updates = {};
      this.giftVariantIds.forEach((id) => {
        if (inCart.includes(id)) updates[id] = 0;
      });
      if (Object.keys(updates).length > 0 || cfg.state === 'active') {
        this.updateCart(updates, { _sample_gifting: '' });
      }
      return;
    }

    // ---- Qualifying: auto-add fixed gift(s) once per qualifying session ----
    if (cfg.fixedEnabled && cfg.state !== 'active') {
      const updates = {};
      (cfg.fixedGifts || []).forEach((g) => {
        // Unavailable fixed gifts are silently skipped.
        if (g.available && !inCart.includes(String(g.variantId))) {
          updates[String(g.variantId)] = 1;
        }
      });
      if (Object.keys(updates).length > 0) {
        this.updateCart(updates, { _sample_gifting: 'active' });
        return;
      }
      // Nothing available to add — mark active so we don't re-check every load,
      // but only if there were fixed gifts configured at all.
      if ((cfg.fixedGifts || []).length > 0) {
        this.updateCart({}, { _sample_gifting: 'active' });
        return;
      }
    }

    // ---- Steady state: wire the picker + the removed-gift prompt ----
    this.renderPickerState(inCart);
    this.maybeShowFixedPrompt(inCart);
    this.bindPicker();
  }

  /** Reflect current cart selection onto the pool thumbnails and enforce the max. */
  renderPickerState(inCart) {
    const cfg = this.config;
    const selectedCount = this.poolVariantIds.filter((id) => inCart.includes(id)).length;
    const atMax = selectedCount >= cfg.pickCount;

    this.querySelectorAll('.sample-gifting__option').forEach((btn) => {
      const vid = String(btn.dataset.variantId);
      const available = btn.dataset.available === 'true';
      const selected = inCart.includes(vid);

      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

      // OOS is always unselectable; otherwise disable non-selected options once at max.
      const disabled = !available || (atMax && !selected);
      btn.classList.toggle('is-disabled', disabled);
      btn.disabled = disabled;
    });

    const counter = this.querySelector('[data-sample-gifting-count]');
    if (counter) counter.textContent = `${selectedCount}/${cfg.pickCount}`;
  }

  /** If a fixed gift was auto-added then manually removed, offer a one-tap re-add. */
  maybeShowFixedPrompt(inCart) {
    const cfg = this.config;
    const prompt = this.querySelector('[data-sample-gifting-prompt]');
    if (!prompt) return;

    const missing =
      cfg.fixedEnabled &&
      cfg.state === 'active' &&
      (cfg.fixedGifts || []).some((g) => g.available && !inCart.includes(String(g.variantId)));

    prompt.hidden = !missing;
  }

  bindPicker() {
    if (this._bound) return;
    this._bound = true;

    this.querySelectorAll('.sample-gifting__option').forEach((btn) => {
      btn.addEventListener('click', () => this.onOptionClick(btn));
    });

    const promptBtn = this.querySelector('[data-sample-gifting-prompt] button');
    if (promptBtn) {
      promptBtn.addEventListener('click', () => this.onAddFixed());
    }
  }

  onOptionClick(btn) {
    if (SampleGifting.busy || btn.disabled) return;
    const vid = String(btn.dataset.variantId);
    const inCart = this.cartVariantIds();
    const selected = inCart.includes(vid);

    if (selected) {
      this.setLoading(btn, true);
      this.updateCart({ [vid]: 0 }, {});
    } else {
      const selectedCount = this.poolVariantIds.filter((id) => inCart.includes(id)).length;
      if (selectedCount >= this.config.pickCount) return;
      this.setLoading(btn, true);
      this.updateCart({ [vid]: 1 }, {});
    }
  }

  onAddFixed() {
    if (SampleGifting.busy) return;
    const inCart = this.cartVariantIds();
    const updates = {};
    (this.config.fixedGifts || []).forEach((g) => {
      if (g.available && !inCart.includes(String(g.variantId))) updates[String(g.variantId)] = 1;
    });
    if (Object.keys(updates).length > 0) this.updateCart(updates, {});
  }

  /**
   * Mirror the checkout £0.00 in the cart: show each gift line at £0.00 and net the
   * gifts out of the drawer total. The native automatic BXGY discount only zeroes at
   * checkout, so this is a display layer over the real cart (proposal section 7).
   *
   * Re-runs on every cart re-render (connectedCallback fires each time cart.js swaps
   * #CartDrawerBody). The total is recomputed from each line's authoritative
   * data-price/data-quantity (never from already-modified text), so it is idempotent.
   * Only applies when the cart qualifies — non-qualifying carts have no gift lines, and
   * this guard also protects a sellable-gift bought standalone (it only reads £0.00 once
   * a qualifying fragrance makes the discount actually apply).
   */
  applyFreePricing() {
    const giftSet = new Set(this.giftVariantIds);
    const hasGiftLine = Array.from(document.querySelectorAll('.cart-item[data-variant-id]')).some((l) =>
      giftSet.has(String(l.dataset.variantId))
    );
    if (!hasGiftLine) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`)
      .then((r) => r.json())
      .then((cart) => this.renderFreePricing(cart, giftSet))
      .catch(() => {});
  }

  /**
   * Reflect Shopify's REAL discounted numbers in the drawer (not a guess):
   *  - a gift line reads £0.00 only when Shopify has actually discounted it (final !=
   *    original), so it can never over-promise (e.g. a sample beyond the discount's
   *    quantity cap keeps its real price);
   *  - the total is set to the cart's real post-discount total;
   *  - the gifting discount's line tag is removed everywhere it renders (incl. the
   *    trigger line, where it's misleading), leaving other discounts untouched
   *    (proposal section 7: no discount string on the line).
   */
  renderFreePricing(cart, giftSet) {
    const byVariant = {};
    (cart.items || []).forEach((i) => {
      byVariant[String(i.variant_id)] = i;
    });

    // The gifting discount's title, read from a gift line (gift lines only carry it).
    let giftDiscountTitle = null;

    document.querySelectorAll('.cart-item[data-variant-id]').forEach((line) => {
      if (!giftSet.has(String(line.dataset.variantId))) return;
      const item = byVariant[String(line.dataset.variantId)];
      if (!item) return;

      if (!giftDiscountTitle) {
        const d = line.querySelector('.cart-item__discounts .discount');
        if (d) giftDiscountTitle = d.textContent.trim();
      }

      if (item.final_line_price !== item.original_line_price) {
        const priceEl = line.querySelector('.cart-item__prices .price');
        if (priceEl) {
          priceEl.classList.remove('price--on-sale');
          priceEl.textContent = this.formatMoney(item.final_line_price);
        }
      }
    });

    if (giftDiscountTitle) {
      document.querySelectorAll('.cart-item__discounts').forEach((ul) => {
        ul.querySelectorAll('.discount').forEach((d) => {
          if (d.textContent.trim() === giftDiscountTitle) (d.closest('li') || d).remove();
        });
        if (!ul.querySelector('.discount')) ul.style.display = 'none';
      });
    }

    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      const suffix = (el.textContent.match(/([A-Z]{2,3})\s*$/) || [null, ''])[1];
      el.textContent = this.formatMoney(cart.total_price) + (suffix ? ' ' + suffix : '');
    });
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  /** Inject the optional "Free gift" badge onto gift lines in the drawer. */
  applyBadges() {
    if (!this.config.badge || !this.config.badge.enabled) return;
    const text = this.config.badge.text || 'Free gift';

    document.querySelectorAll('.cart-item[data-variant-id]').forEach((line) => {
      const vid = String(line.dataset.variantId);
      if (!this.giftVariantIds.includes(vid)) return;
      const details = line.querySelector('.cart-item__details .grid') || line.querySelector('.cart-item__details');
      if (!details || details.querySelector('.sample-gift-badge')) return;
      const badge = document.createElement('span');
      badge.className = 'sample-gift-badge';
      badge.textContent = text;
      const title = details.querySelector('.block');
      if (title) {
        title.appendChild(badge);
      } else {
        details.prepend(badge);
      }
    });
  }

  setLoading(el, state) {
    if (el) el.classList.toggle('is-loading', state);
  }

  getSectionsToUpdate() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  updateCart(updates, attributes) {
    SampleGifting.busy = true;
    const body = JSON.stringify({
      updates,
      attributes,
      sections: this.getSectionsToUpdate(),
    });

    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        SampleGifting.busy = false;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
      })
      .catch((error) => {
        SampleGifting.busy = false;
        console.error('[sample-gifting] cart update failed', error);
      });
  }
}

customElements.define('sample-gifting', SampleGifting);
